import { IdentityError, type IdentityService, type PlayerProfile } from '../network/IdentityService';
import { StationService, type StationMessage, type StationUser } from '../network/StationService';

const pollIntervalMs = 2_500;
const historyLimit = 80;

export class StationChatFlow {
  private readonly panel: HTMLElement;
  private readonly onlineCount: HTMLElement;
  private readonly onlineList: HTMLElement;
  private readonly mutedPanel: HTMLElement;
  private readonly mutedList: HTMLElement;
  private readonly messages: HTMLOListElement;
  private readonly composer: HTMLInputElement;
  private readonly sendButton: HTMLButtonElement;
  private readonly error: HTMLElement;
  private readonly service: StationService;
  private history: StationMessage[] = [];
  private online: StationUser[] = [];
  private muted: StationUser[] = [];
  private cursor = 0;
  private readonly muteBusy = new Set<string>();
  private readonly reportBusy = new Set<number>();
  private readonly reported = new Set<number>();
  private pendingReportID = 0;
  private reportReset = 0;
  private polling = 0;
  private active = false;
  private pollBusy = false;
  private pollAbort: AbortController | null = null;
  private sending = false;
  private filterRevision = 0;
  private available = true;

  public constructor(
    root: HTMLElement,
    config: ConstructorParameters<typeof StationService>[0],
    private readonly identity: IdentityService,
    private readonly canEnter: () => boolean = () => true,
  ) {
    this.service = new StationService(config, identity);
    this.panel = required(root, '[data-station-channel]');
    this.onlineCount = required(root, '[data-station-online-count]');
    this.onlineList = required(root, '[data-station-online]');
    this.mutedPanel = required(root, '[data-station-muted-panel]');
    this.mutedList = required(root, '[data-station-muted]');
    this.messages = required(root, '[data-station-messages]');
    this.composer = required(root, '[data-station-message]');
    this.sendButton = required(root, '[data-station-send]');
    this.error = required(root, '[data-station-error]');
    this.sendButton.addEventListener('click', () => void this.send());
    this.composer.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' && !event.repeat) void this.send();
    });
    this.identity.subscribe(this.renderIdentity);
    window.addEventListener('pagehide', () => this.leave(true));
  }

  public enter(): void {
    if (this.active || !this.canEnter() || this.identity.current()?.guest !== false) return;
    this.active = true;
    this.error.textContent = '';
    void this.poll();
    this.polling = window.setInterval(() => void this.poll(), pollIntervalMs);
  }

  public leave(keepalive = false): void {
    const wasActive = this.active;
    this.active = false;
    this.pollAbort?.abort();
    this.pollAbort = null;
    this.pollBusy = false;
    window.clearInterval(this.polling);
    this.polling = 0;
    if (wasActive && this.identity.authorization()) void this.service.leave(keepalive).catch(() => undefined);
  }

  private readonly renderIdentity = (profile: PlayerProfile | null): void => {
    if (profile?.guest === false) {
      this.enter();
      return;
    }
    this.leave();
    this.composer.value = '';
    this.history = [];
    this.online = [];
    this.muted = [];
    this.available = true;
    this.reported.clear();
    this.pendingReportID = 0;
    window.clearTimeout(this.reportReset);
    this.cursor = 0;
    this.render();
  };

  private async poll(): Promise<void> {
    if (!this.active || this.pollBusy || this.panel.offsetParent === null) return;
    this.pollBusy = true;
    const controller = new AbortController();
    this.pollAbort = controller;
    const revision = this.filterRevision;
    try {
      const snapshot = await this.service.snapshot(this.cursor, controller.signal);
      if (!this.active || revision !== this.filterRevision) return;
      if (snapshot.available === false) {
        this.available = false;
        this.history = [];
        this.online = [];
        this.muted = [];
        this.cursor = 0;
        this.error.dataset.kind = 'danger';
        this.error.textContent = 'ACCOUNT ALREADY ASSIGNED TO A CREW // RETURN OR LEAVE THAT RUN TO REOPEN COMMS.';
        this.render();
        return;
      }
      this.available = true;
      this.applySnapshot(snapshot);
      this.error.textContent = '';
      this.render();
    } catch (error) {
      if (this.active && !controller.signal.aborted) this.showError(error);
    } finally {
      if (this.pollAbort === controller) {
        this.pollAbort = null;
        this.pollBusy = false;
      }
    }
  }

  private async send(): Promise<void> {
    const body = this.composer.value.trim();
    if (!body || !this.active || !this.available || this.sending) return;
    this.sending = true;
    this.sendButton.disabled = true;
    try {
      const message = await this.service.send(body);
      this.composer.value = '';
      if (!this.history.some((existing) => existing.id === message.id)) this.history.push(message);
      if (this.history.length > historyLimit) this.history = this.history.slice(-historyLimit);
      this.error.textContent = '';
      this.render();
    } catch (error) {
      this.showError(error);
    } finally {
      this.sending = false;
      this.sendButton.disabled = !this.available;
    }
  }

  private render(): void {
    const previousScrollTop = this.messages.scrollTop;
    const wasNearBottom = this.history.length === 0
      || this.messages.scrollHeight - this.messages.clientHeight - this.messages.scrollTop < 20;
    this.onlineCount.textContent = String(this.online.length);
    this.composer.disabled = !this.available;
    this.sendButton.disabled = !this.available || this.sending;
    this.onlineList.replaceChildren();
    if (!this.online.length) {
      const empty = document.createElement('span');
      empty.textContent = 'NO ACTIVE SIGNALS';
      this.onlineList.append(empty);
    } else {
      const myID = this.identity.current()?.id;
      for (const user of this.online) {
        this.onlineList.append(this.createSignal(user, user.userId === myID));
      }
    }
    const onlineIDs = new Set(this.online.map((user) => user.userId));
    const offlineMuted = this.muted.filter((user) => !onlineIDs.has(user.userId));
    this.mutedPanel.hidden = offlineMuted.length === 0;
    this.mutedList.replaceChildren(...offlineMuted.map((user) => this.createSignal(user, false, true)));
    this.messages.replaceChildren();
    if (!this.history.length) {
      const empty = document.createElement('li');
      empty.className = 'is-system';
      empty.textContent = this.available
        ? 'PLATFORM CHANNEL OPEN. FIND A CREW OR BOARD A PUBLIC RUN.'
        : 'PUBLIC CHANNEL HELD // THIS ACCOUNT HAS AN AUTHORITATIVE SEAT ON ANOTHER TRAIN.';
      this.messages.append(empty);
      return;
    }
    const myID = this.identity.current()?.id;
    for (const message of this.history) {
      const row = document.createElement('li');
      row.classList.toggle('is-mine', message.userId === myID);
      const heading = document.createElement('header');
      const name = document.createElement('b');
      name.textContent = message.displayName;
      const time = document.createElement('time');
      time.dateTime = message.createdAt;
      time.textContent = formatMessageTime(message.createdAt);
      const body = document.createElement('p');
      body.textContent = message.body;
      heading.append(name, time);
      if (message.userId !== myID) heading.append(this.createReportButton(message));
      row.append(heading, body);
      this.messages.append(row);
    }
    this.messages.scrollTop = wasNearBottom
      ? this.messages.scrollHeight
      : Math.min(previousScrollTop, Math.max(0, this.messages.scrollHeight - this.messages.clientHeight));
  }

  private showError(error: unknown): void {
    this.error.dataset.kind = 'danger';
    this.error.textContent = error instanceof IdentityError ? error.message : 'The Station channel lost signal.';
  }

  private createReportButton(message: StationMessage): HTMLButtonElement {
    const button = document.createElement('button');
    const sent = this.reported.has(message.id);
    const confirming = this.pendingReportID === message.id;
    button.type = 'button';
    button.className = 'station-message-report';
    button.disabled = sent || this.reportBusy.has(message.id);
    button.textContent = sent ? 'SENT' : confirming ? 'CONFIRM' : 'REPORT';
    button.setAttribute('aria-label', sent
      ? `${message.displayName} public signal reported`
      : confirming
        ? `Confirm report of ${message.displayName} public signal`
        : `Report ${message.displayName} public signal`);
    button.addEventListener('click', () => void this.report(message));
    return button;
  }

  private async report(message: StationMessage): Promise<void> {
    if (this.reportBusy.has(message.id) || this.reported.has(message.id) || message.userId === this.identity.current()?.id) return;
    if (this.pendingReportID !== message.id) {
      this.pendingReportID = message.id;
      window.clearTimeout(this.reportReset);
      this.reportReset = window.setTimeout(() => {
        if (this.pendingReportID !== message.id) return;
        this.pendingReportID = 0;
        this.render();
      }, 5_000);
      this.render();
      return;
    }
    this.reportBusy.add(message.id);
    window.clearTimeout(this.reportReset);
    this.render();
    try {
      await this.service.report(message.id);
      this.reported.add(message.id);
      this.pendingReportID = 0;
      this.error.dataset.kind = 'success';
      this.error.textContent = 'SIGNAL REPORTED // EVIDENCE SEALED FOR YARD REVIEW.';
    } catch (error) {
      this.pendingReportID = 0;
      this.showError(error);
    } finally {
      this.reportBusy.delete(message.id);
      this.render();
    }
  }

  private async setMuted(user: StationUser): Promise<void> {
    if (this.muteBusy.has(user.userId) || user.userId === this.identity.current()?.id) return;
    this.muteBusy.add(user.userId);
    const previousHistory = this.history;
    const previousOnline = this.online;
    const previousMuted = this.muted;
    const previousCursor = this.cursor;
    const muted = !user.muted;
    const revision = ++this.filterRevision;
    this.online = this.online.map((candidate) => candidate.userId === user.userId ? { ...candidate, muted } : candidate);
    this.muted = muted
      ? [...this.muted.filter((candidate) => candidate.userId !== user.userId), { ...user, muted: true }]
      : this.muted.filter((candidate) => candidate.userId !== user.userId);
    if (muted) this.history = this.history.filter((message) => message.userId !== user.userId);
    else this.history = [];
    this.cursor = 0;
    this.render();
    let mutationApplied = false;
    try {
      await this.service.setMuted(user.userId, muted);
      mutationApplied = true;
      const snapshot = await this.service.snapshot(0);
      if (revision !== this.filterRevision) return;
      this.applySnapshot(snapshot, true);
      this.error.textContent = muted
        ? `${user.displayName.toUpperCase()} MUTED // THEIR PUBLIC SIGNALS ARE HIDDEN.`
        : `${user.displayName.toUpperCase()} RESTORED TO THE PLATFORM CHANNEL.`;
    } catch (error) {
      this.filterRevision += 1;
      if (!mutationApplied) {
        this.online = previousOnline;
        this.muted = previousMuted;
        this.history = previousHistory;
        this.cursor = previousCursor;
      }
      this.showError(error);
    } finally {
      this.muteBusy.delete(user.userId);
      this.render();
    }
  }

  private applySnapshot(snapshot: Awaited<ReturnType<StationService['snapshot']>>, replace = false): void {
    this.online = snapshot.online;
    this.muted = snapshot.muted ?? [];
    this.cursor = replace ? snapshot.cursor ?? 0 : Math.max(this.cursor, snapshot.cursor ?? 0);
    if (replace) {
      this.history = snapshot.messages.slice(-historyLimit);
      return;
    }
    if (!snapshot.messages.length) return;
    const known = new Set(this.history.map((message) => message.id));
    this.history.push(...snapshot.messages.filter((message) => !known.has(message.id)));
    if (this.history.length > historyLimit) this.history = this.history.slice(-historyLimit);
  }

  private createSignal(user: StationUser, mine: boolean, offline = false): HTMLElement {
    const tag = document.createElement(mine ? 'span' : 'button');
    tag.className = 'station-signal';
    tag.classList.toggle('is-you', mine);
    tag.classList.toggle('is-muted', Boolean(user.muted));
    tag.classList.toggle('is-offline', offline);
    const signal = document.createElement('i');
    const name = document.createElement('b');
    name.textContent = user.displayName;
    tag.append(signal, name);
    if (tag instanceof HTMLButtonElement) {
      tag.type = 'button';
      tag.disabled = this.muteBusy.has(user.userId);
      tag.setAttribute('aria-label', `${user.muted ? 'Unmute' : 'Mute'} ${user.displayName} in Station chat`);
      tag.title = `${user.muted ? 'Restore' : 'Hide'} ${user.displayName}'s public signals`;
      const action = document.createElement('small');
      action.textContent = user.muted ? 'UNMUTE' : 'MUTE';
      tag.append(action);
      tag.addEventListener('click', () => void this.setMuted(user));
    }
    return tag;
  }
}

function formatMessageTime(value: string): string {
  return new Intl.DateTimeFormat(undefined, { hour: '2-digit', minute: '2-digit' }).format(new Date(value));
}

function required<T extends Element>(root: HTMLElement, selector: string): T {
  const element = root.querySelector<T>(selector);
  if (!element) throw new Error('Missing Station chat element: ' + selector);
  return element;
}
